import type { OrderStatus, OrderType } from "@loot-os/fulfillment-core/order/types";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { transitionTargets } from "../lib/orderActions";
import { useTRPC } from "../lib/trpc";

/**
 * Walks an order along the state machine and carries the tracking number with
 * the move, which is the only way the dashboard writes one. `fromStatus` makes
 * the write conflict rather than skip a step when someone else moved it first.
 */
export function OrderTransitionActions({
  order,
}: {
  order: { id: string; status: OrderStatus; type: OrderType; trackingNumber: string | null };
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [trackingNumber, setTrackingNumber] = useState(order.trackingNumber ?? "");

  const transition = useMutation(
    trpc.platform.fulfillment.orders.transition.mutationOptions({
      onSuccess: (updated) => {
        toast.success(`Order is ${updated.status}.`);
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.orders.pathKey(),
        });
      },
    }),
  );

  const targets = transitionTargets(order.status, order.type);
  if (targets.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Move this order on</CardTitle>
        <CardDescription>
          PROCESSING once the buy is placed, PRESHIPPED once the vendor verified the item, SHIPPED
          once it is on its way. The tracking number below is saved with the move.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid max-w-sm gap-2">
          <Label htmlFor="tracking-number">Tracking number</Label>
          <Input
            id="tracking-number"
            value={trackingNumber}
            disabled={transition.isPending}
            onChange={(e) => setTrackingNumber(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {targets.map((target) => (
            <Button
              key={target}
              type="button"
              variant="outline"
              disabled={transition.isPending}
              onClick={() =>
                transition.mutate({
                  id: order.id,
                  toStatus: target,
                  fromStatus: order.status,
                  trackingNumber: trackingNumber.trim() || undefined,
                })
              }
            >
              {target}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
